import { MouseEventHandler } from 'react';

import upperFirst from 'lodash/upperFirst';

import { Button } from '@spotify-internal/encore-web';

import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { WorkflowCreditGuard } from '../WorkflowCreditGuard';
import { formatAction } from './ActionButtonsList';
import { useActionButtonState } from './useActionButtonState';

type ActionButtonProps = {
  action: string;
  disabled?: boolean;
  primary?: boolean;
  recommended?: boolean;
  disabledReason?: string | null;
  workflowInstance: Pick<
    WorkflowInstance,
    'pendingTransition' | 'transitionStatus' | 'checks'
  >;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

export const recommendedActionTooltip =
  'Recommended based on the analysis results';

export const getButtonStyleProps = ({
  primary,
  recommended,
}: {
  primary?: boolean;
  recommended?: boolean;
} = {}):
  | { variant: 'primary'; colorSet?: 'inverted' }
  | { variant: 'secondary-bordered' } => {
  if (primary) {
    return { variant: 'primary' };
  }
  if (recommended) {
    return { variant: 'primary', colorSet: 'inverted' };
  }
  return { variant: 'secondary-bordered' };
};

export const ActionButton = ({
  action,
  primary,
  recommended,
  onClick,
  disabled,
  disabledReason,
  workflowInstance,
}: ActionButtonProps) => {
  const disabledState = [
    { disabled, disabledReason },
    useActionButtonState({ action, workflowInstance }),
  ].find(state => state.disabled);

  const isDisabled = !!disabledState?.disabled;

  const tooltipTitle = isDisabled
    ? disabledState?.disabledReason || ''
    : (recommended && recommendedActionTooltip) || '';

  return (
    <WorkflowCreditGuard
      workflowInstance={workflowInstance}
      tooltip={tooltipTitle}
    >
      <Button
        data-track-source-anchor="87824a34c9;action-button" {...getButtonStyleProps({ primary, recommended })}
        isVisuallyDisabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={onClick}
        data-track-id={`button.${action}`}
        data-track-action="transition"
      >
        {upperFirst(formatAction(action))}
      </Button>
    </WorkflowCreditGuard>
  );
};
